import {
  AnimatedContentLoader,
  Button,
  DocumentCopyDropdown,
  InlineTeaserView,
  Input,
  Prompt,
  Select
} from '@components'
import { motion } from 'framer-motion'
import { ContactSelector } from '@components/uas/ContactSelector'
import {
  BlueprintType,
  BLUEPRINT_TYPE_OPTIONS,
  NOCFO_PLAN_OPTIONS,
  getDocumentsPageUrl,
  getSettingsPageUrl
} from '@constants'
import { ControlledDocumentAttachmentsField } from './DocumentAttachmentsField'
import { PermissionSet, useBusinessContext, usePermission } from '@context'
import { useQueryParams, useDocumentLimitStatus } from '@hooks'
import {
  createDocument,
  fetchDocument,
  fetchBankIntegrations,
  IDocument,
  invalidateDashboardQueries,
  isBusinessBillingLocked,
  updateDocument,
  useInvalidateDocumentLimits,
  useInvalidateTrialLimits,
  useTrialLimits
} from '@query'
import { IAccountingSuggestion } from '@query/suggestions'
import { getOptionLabelByValue, setAPIErrors, useScreen } from '@utils'
import { getDocumentCopyValues } from '@utils/copyDocument'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaSave, FaUndo, FaExclamationTriangle } from 'react-icons/fa'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { fetchHolviAccounts } from '@query/holvi'
import { BlueprintEditor } from './BlueprintEditor'
import { DocumentHelpLink } from './DocumentHelpLink'
import { EuB2bVatFieldWrap, EuB2bVatWarning } from './EuB2bVatWarning'
import { queryKeyForAuditTrail } from '../DocumentDetails/DocumentAuditTrail'
import { cleanBlueprint } from '@utils'
import { useAllowUpload } from '@root/hooks/useAllowUpload'
import { TagSelector } from '@root/containers/TagSelector/TagSelector'
import {
  BlueprintDebetCreditDiffSync,
  BlueprintPaymentAmountSync,
  ConfirmPostingWarningReason,
  expectedAmountForPaymentAccount,
  getConfirmPostingWarning,
  postingWarningDescriptionKey,
  postingWarningTitleKey,
  useUpdateEntryAmounts
} from './hooks'
import { useEuSaleWarning } from './euB2bVatDetails'
import { FormSectionEyebrow } from './FormSectionEyebrow'
import { RelationMapper, DocumentRelationFormValue } from './RelationMapper'
import { RelationsHeader } from './RelationMapper.styled'
import { getDocumentRelationContext, mapRelationsForForm } from './relationMapperUtils'

interface Props {
  documentId?: number
  suggestion?: IAccountingSuggestion
  onClose: () => void
  onCreated?: (document: IDocument) => void
  defaults?: Partial<IDocument>
  onValuesUpdate?: (values: DocumentFormValues) => void
  enableCache?: boolean
  contactSuggestion?: string
}

type DocumentFormValues = Partial<Omit<IDocument, 'relations'>> & {
  relations?: DocumentRelationFormValue[]
}

const BANK_ACCOUNT_QUERY_STALE_TIME_MS = 24 * 60 * 60 * 1000

export const DocumentForm: React.FC<Props> = ({
  documentId,
  suggestion,
  defaults = {},
  onClose,
  onCreated,
  onValuesUpdate,
  contactSuggestion
}) => {
  const { t } = useTranslation()
  const { slug, data: business } = useBusinessContext()
  const canManageIntegrations = usePermission(PermissionSet.INTEGRATION_ADMIN)
  const queryClient = useQueryClient()
  const history = useHistory()
  const isEdit = !!documentId
  const [params] = useQueryParams()
  const invalidateDocumentLimits = useInvalidateDocumentLimits()
  const invalidateTrialLimits = useInvalidateTrialLimits()

  // DOCUMENT FETCHING
  // =================

  const { data: document, refetch } = useQuery({
    queryKey: [slug, 'documents', documentId],
    queryFn: () => fetchDocument({ slug, documentId }),
    enabled: false
  })
  const isManualCreation = !isEdit && !document?.import_data && !suggestion?.import_data
  const { data: bankIntegrationsData } = useQuery({
    queryKey: [slug, 'bank_integrations'],
    queryFn: () => fetchBankIntegrations({ slug }),
    enabled: isManualCreation && canManageIntegrations,
    staleTime: BANK_ACCOUNT_QUERY_STALE_TIME_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  })
  const { data: holviAccountsData } = useQuery({
    queryKey: [slug, 'holvi', 'accounts'],
    queryFn: () => fetchHolviAccounts({ slug }),
    enabled:
      isManualCreation && canManageIntegrations && Boolean(business?.holvi_bookkeeping_api_enabled),
    staleTime: BANK_ACCOUNT_QUERY_STALE_TIME_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  })

  // FORM SETUP
  // ==========

  const getDefaultValues = useCallback(
    (document?: IDocument): DocumentFormValues => {
      const defaultValues: DocumentFormValues = {
        blueprint_type: BlueprintType.PURCHASE,
        date: '',
        description: '',
        contact_id: null,
        blueprint: {},
        attachment_ids: [],
        attachment_exempt: false,
        tag_ids: [],
        ...defaults
      }

      defaultValues.relations = mapRelationsForForm(defaults?.relations as IDocument['relations'])

      if (document) {
        return {
          ...defaultValues,
          blueprint_type: document?.blueprint_type,
          date: document?.date,
          description: document?.description,
          contact_id: document?.contact_id,
          blueprint: document?.blueprint,
          attachment_ids: document?.attachment_ids,
          attachment_exempt: document?.attachment_exempt ?? false,
          tag_ids: document?.tag_ids,
          relations: mapRelationsForForm(document?.relations)
        }
      }

      return defaultValues
    },
    [defaults]
  )

  const methods = useForm<DocumentFormValues>({
    defaultValues: async () => {
      if (isEdit) {
        const { data: document } = await refetch()
        return getDefaultValues(document)
      } else {
        return getDefaultValues()
      }
    }
  })

  const {
    control,
    register,
    setError,
    getValues,
    setValue,
    handleSubmit,
    reset,
    formState: { errors, isLoading: isFormLoading }
  } = methods

  const updateEntryAmounts = useUpdateEntryAmounts(getValues, setValue)

  const [showAcceptAmountConfirm, setShowAcceptAmountConfirm] = useState(false)
  const [confirmReason, setConfirmReason] = useState<ConfirmPostingWarningReason | null>(null)
  const pendingSubmitDataRef = useRef<Partial<IDocument> | null>(null)
  const calculatedAmountRef = useRef<number>(0)
  const debetCreditDiffRef = useRef<number | null>(null)

  const closeConfirmPrompt = useCallback(() => {
    setShowAcceptAmountConfirm(false)
    setConfirmReason(null)
    pendingSubmitDataRef.current = null
  }, [])

  const isAllowedToUpload = useAllowUpload()

  // WATCH VALUES
  // ============

  const values = useWatch({ control }) as Partial<IDocument>
  const watchedDate = useWatch({
    control,
    name: 'date'
  })
  const contactId = useWatch({
    control,
    name: 'contact_id'
  })
  const blueprintType = useWatch({
    control,
    name: 'blueprint_type'
  })
  const blueprint = useWatch({
    control,
    name: 'blueprint'
  })
  const euSaleWarning = useEuSaleWarning(contactId, blueprint)
  const { documentLimits, isDocumentLimitReached } = useDocumentLimitStatus(watchedDate)
  const isTrialing = business?.stripe_plan_status === 'trialing'
  const { data: trialLimits } = useTrialLimits({ enabled: isTrialing })
  const trialLimitReached =
    isTrialing &&
    trialLimits?.document_usage?.limit != null &&
    (trialLimits?.document_usage?.remaining ?? 0) <= 0
  const planName = getOptionLabelByValue(NOCFO_PLAN_OPTIONS, business?.subscription_plan, {
    t,
    fallback: '',
    fallbackToValue: true
  })
  const planSettingsUrl = getSettingsPageUrl(slug, { page: 'plan' })
  useEffect(() => {
    if (onValuesUpdate) onValuesUpdate(values)
  }, [JSON.stringify(values)])

  // FORM RESET
  // ==========

  const resetForm = useCallback(() => {
    const options = {
      keepErrors: false,
      keepDirty: false,
      keepDirtyValues: false,
      keepValues: false,
      keepDefaultValues: true
    }
    reset(undefined, options)

    // For some reason the above code won't reset field arrays
    // but this thing here fixes it:
    if (!isEdit)
      reset(
        {
          blueprint: {
            debet_account_id: null,
            credit_account_id: null,
            debet_entries: [{}] as any,
            credit_entries: [{}] as any
          }
        },
        options
      )
  }, [])

  // CONTACT SUGGESTION
  // ==================

  // Enable suggestion if document is already loaded, document is not draft
  // and contact is not already selected
  const contact_hint = document?.import_data?.contact_hint || contactSuggestion
  const enableContactSuggestion = !!contact_hint && !contactId

  // COPY ANOTHER DOCUMENT
  // =====================

  const onCopy = useCallback(documentToCopy => {
    reset(
      {
        ...getValues(),
        ...getDocumentCopyValues(documentToCopy, isEdit ? document.import_data : null)
      },
      { keepDefaultValues: true }
    )
  }, [])

  // CREATION & UPDATING
  // ===================

  const options = useMemo(
    () => ({
      onSuccess: async (data: IDocument) => {
        queryClient.invalidateQueries({ queryKey: [slug, 'documents'] })
        queryClient.invalidateQueries({ queryKey: queryKeyForAuditTrail(slug, data.id) })
        invalidateDocumentLimits(data.date)
        invalidateTrialLimits()
        await queryClient.invalidateQueries({ queryKey: [slug, 'documents', data.id] })
        await invalidateDashboardQueries(queryClient, slug)
        if (!data.is_draft) {
          void queryClient.invalidateQueries({ queryKey: [slug], exact: true })
        }

        if (!isEdit) resetForm()
        onClose()

        if (onCreated && !isEdit) {
          onCreated(data)
        } else {
          history.push(getDocumentsPageUrl(slug, { ...params, id: data.id }))
        }
        toast.success(isEdit ? t('document.form.updateSuccess') : t('document.form.createSuccess'))
      },
      onError: error => {
        const { status, data } = (error as any) ?? {}
        if (status === 400) {
          setAPIErrors(data, setError)

          if (data.blueprint?.common) {
            toast.error(data.blueprint?.common[0])
          } else if (data.non_field_errors) {
            toast.error(data.non_field_errors[0])
          }
        } else {
          toast.error(t('document.form.updateError'))
        }
      }
    }),
    [params, onClose, onCreated]
  )
  const connectedBankAccountIds = useMemo(() => {
    const accountIds = new Set<number>()

    for (const integration of bankIntegrationsData?.results || []) {
      if (!integration.enabled) continue

      for (const account of integration.accounts || []) {
        if (account.enabled) {
          accountIds.add(account.account_id)
        }
      }
    }

    for (const account of holviAccountsData?.results || []) {
      if (account.enabled) {
        accountIds.add(account.account)
      }
    }

    return accountIds
  }, [bankIntegrationsData, holviAccountsData])

  const createMutation = useMutation<IDocument, unknown, Partial<IDocument>>({
    mutationFn: data => createDocument({ slug }, data),
    ...options
  })
  const updateMutation = useMutation<IDocument, unknown, Partial<IDocument>>({
    mutationFn: data => updateDocument({ slug, documentId: document.id }, data),
    ...options
  })
  const onSubmit = handleSubmit(data => {
    const formattedRelations =
      data.relations?.map(({ id, related_document, role, type }) => ({
        ...(id ? { id } : {}),
        related_document,
        role,
        type
      })) || []

    const newData = {
      ...data,
      relations: formattedRelations,
      blueprint: cleanBlueprint(data?.blueprint_type, data?.blueprint)
    } as unknown as Partial<IDocument>

    const warning = getConfirmPostingWarning({
      expectedAmount: expectedAmountForPaymentAccount(document?.import_data),
      calculatedAmount: calculatedAmountRef.current,
      debetCreditDiff: debetCreditDiffRef.current,
      blueprint: newData.blueprint,
      isManualCreation,
      connectedBankAccountIds,
      euSaleWarning
    })
    if (warning) {
      pendingSubmitDataRef.current = newData
      setConfirmReason(warning.reason)
      setShowAcceptAmountConfirm(true)
      return
    }

    isEdit ? updateMutation.mutate(newData) : createMutation.mutate(newData)
  })

  const { md } = useScreen()
  const isBillingLocked = isBusinessBillingLocked(business)

  // Only block on initial form hydration — not background document refetches from
  // RelationMapper / side panel observers sharing the same query key.
  const isLoading = isFormLoading

  const relationPreviewData = useMemo(
    () => ({
      date: watchedDate,
      contactId,
      blueprint,
      excludeDocumentIds: documentId ? [documentId] : []
    }),
    [watchedDate, contactId, blueprint, documentId]
  )
  const savedRelationContext = useMemo(
    () => (document ? getDocumentRelationContext(document) : null),
    [document]
  )
  return (
    <AnimatedContentLoader isLoading={isLoading}>
      <FormProvider {...methods}>
        <BlueprintPaymentAmountSync
          paymentAccountId={document?.import_data?.payment_account_id}
          calculatedAmountRef={calculatedAmountRef}
        />
        <BlueprintDebetCreditDiffSync debetCreditDiffRef={debetCreditDiffRef} />
        <StyledForm onSubmit={onSubmit} data-test="document-form">
          <StyledFormContent>
            {!md && !isBillingLocked && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
                <DocumentHelpLink />
              </div>
            )}

            {/* DATE */}
            <Input
              data-test="document-form-date-input"
              type="date"
              id="document-date"
              placeholder="YYYY-MM-DD"
              autoFocus={true}
              label={t('document.date')}
              info={t('document.dateInfo')}
              required={true}
              {...register('date', {
                required: t('validation.required') as string
              })}
              errors={errors.date}
            />

            {trialLimitReached ? (
              <LimitNoticeWrapper>
                <InlineTeaserView
                  icon={<FaExclamationTriangle />}
                  iconText={t('document.form.trialLimitReachedTitle')}
                  text={t('document.form.trialLimitReachedDescription', { planName })}
                  ctaLabel={t('document.form.trialLimitReachedCta')}
                  ctaTo={planSettingsUrl}
                />
              </LimitNoticeWrapper>
            ) : isDocumentLimitReached ? (
              <LimitNoticeWrapper>
                <InlineTeaserView
                  icon={<FaExclamationTriangle />}
                  iconText={t('document.form.limitReachedTitle')}
                  text={t('document.form.limitReachedDescription', {
                    documentLimit: documentLimits?.limit ?? 0
                  })}
                />
              </LimitNoticeWrapper>
            ) : null}

            <motion.div layout="position">
              {/* DESCRIPTION & COPY FUNCTIONALITY */}
              <Controller
                control={control}
                name="description"
                rules={{ required: { value: true, message: t('validation.required') } }}
                render={({ field, fieldState: { error } }) => (
                  <DocumentCopyDropdown
                    key="document-copy-dropdown"
                    label={t('document.description')}
                    placeholder={t('document.descriptionPlaceholder')}
                    info={t('document.descriptionInfo')}
                    id="document-description"
                    slug={slug}
                    onCopy={onCopy}
                    errors={error}
                    required={true}
                    excludeIds={documentId ? [documentId] : null}
                    importModelId={document?.import_data?.id}
                    {...field}
                  />
                )}
              />

              {/* CONTACT & CONTACT SUGGESTIONS */}
              <EuB2bVatFieldWrap $warn={euSaleWarning != null}>
                <Controller
                  name="contact_id"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <ContactSelector
                      {...field}
                      errors={error}
                      ref={null}
                      required={false}
                      isSuggestionEnabled={enableContactSuggestion}
                      suggestionQuery={{
                        query: contact_hint
                      }}
                      fetchOnlyInvoicableContacts={false}
                      placeholder={t('document.contactPlaceholder')}
                      label={t('document.contact')}
                      info={t('document.contactInfo')}
                      noDataMessage={t('contacts.contactSelector.noDataDocumentForm2')}
                      isQuickAddEnabled={true}
                    ></ContactSelector>
                  )}
                />
                {euSaleWarning ? (
                  <EuB2bVatWarning contactId={contactId} variant={euSaleWarning} />
                ) : null}
              </EuB2bVatFieldWrap>

              {/* TAGS */}

              {business?.has_tags ? (
                <Controller
                  name="tag_ids"
                  control={control}
                  render={({ field }) => (
                    <StyledTagWrapper>
                      <TagSelector {...field} />
                    </StyledTagWrapper>
                  )}
                />
              ) : (
                <br />
              )}

              <Controller
                name="blueprint_type"
                control={control}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <Select
                    data-test="document-form-type-select"
                    name="blueprint_type"
                    label={t('document.selectedType')}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      updateEntryAmounts(blueprintType)
                      onChange(event)
                    }}
                    value={value}
                    errors={error}
                    required={true}
                  >
                    {BLUEPRINT_TYPE_OPTIONS.map(({ value, labelKey }) => (
                      <option key={value} value={value}>
                        {t(labelKey)}
                      </option>
                    ))}
                  </Select>
                )}
              />

              <BlueprintWrapper>
                <BlueprintEditor
                  contactId={contactId ?? undefined}
                  expectedPaymentAccount={document?.import_data?.payment_account_id}
                  expectedPaymentAccountAmount={expectedAmountForPaymentAccount(
                    document?.import_data
                  )}
                />
              </BlueprintWrapper>

              <RelationsFormSection>
                <Controller
                  name="relations"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <RelationMapper
                      value={value}
                      onChange={onChange}
                      documentId={documentId}
                      layoutVariant="form"
                      suggestionsEnabled={!documentId || !document?.is_locked}
                      canApplySuggestion={!document?.is_locked}
                      previewData={relationPreviewData}
                      renderHeader={({ title, lead, addMenu, themeMode }) => (
                        <RelationsHeader $themeMode={themeMode}>
                          <FormSectionEyebrow $hasLead={Boolean(lead)}>
                            {lead ?? title}
                          </FormSectionEyebrow>
                          {addMenu}
                        </RelationsHeader>
                      )}
                      {...(savedRelationContext ?? {})}
                    />
                  )}
                />
              </RelationsFormSection>

              <FormSection>
                <FormSectionEyebrow>{t('document.attachments')}</FormSectionEyebrow>

                <ControlledDocumentAttachmentsField
                  control={control}
                  disabled={!isAllowedToUpload}
                />
              </FormSection>
            </motion.div>
          </StyledFormContent>
          <ButtonFooter>
            <Button type="button" onClick={() => onClose()} data-test="document-form-cancel-button">
              {t('general.cancel')}
            </Button>
            <Button
              tabIndex={-1}
              type="button"
              icon={<FaUndo />}
              onClick={() => {
                resetForm()
              }}
            >
              {t('general.startOver')}
            </Button>

            <div style={{ flex: 1 }} />

            <Button
              data-test="document-form-save-button"
              icon={<FaSave />}
              type="submit"
              intent="success"
              disabled={updateMutation.isPending || createMutation.isPending}
              showSpinner={updateMutation.isPending || createMutation.isPending}
            >
              {t('general.save')}
            </Button>
          </ButtonFooter>
        </StyledForm>

        <Prompt
          isVisible={showAcceptAmountConfirm}
          onClose={closeConfirmPrompt}
          title={confirmReason ? t(postingWarningTitleKey(confirmReason)) : undefined}
          description={confirmReason ? t(postingWarningDescriptionKey(confirmReason)) : undefined}
          buttons={[
            {
              text: t('general.cancel'),
              intent: 'default',
              action: closeConfirmPrompt
            },
            {
              text: t('general.save'),
              intent: 'success',
              action: () => {
                const payload = pendingSubmitDataRef.current
                closeConfirmPrompt()
                if (payload) {
                  isEdit ? updateMutation.mutate(payload) : createMutation.mutate(payload)
                }
              }
            }
          ]}
        />
      </FormProvider>
    </AnimatedContentLoader>
  )
}

const BlueprintWrapper = styled.div`
  border: 3px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
`

const ButtonFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
  padding-top: ${({ theme }) => theme.spacing.lg}rem;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const FormSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md}rem;
  margin-bottom: ${({ theme }) => theme.spacing.lg}rem;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const RelationsFormSection = styled(FormSection)`
  margin-top: ${({ theme }) => theme.spacing.lg}rem;
`

const StyledForm = styled.form`
  height: 100%;
  display: flex;
  flex-direction: column;
`

const StyledFormContent = styled.div`
  flex: 1;
  overflow: auto;
`

const StyledTagWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md}rem;
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;
  margin-left: ${({ theme }) => theme.spacing.xxxs}rem;
`

const LimitNoticeWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm}rem;
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;
`
